import Phaser from 'phaser';
import ScreenContainer from '../display/ui/ScreenContainer';
import { STATE_LOCATION, TUTORIAL_LOCATION } from '../constants/AppConstants';
import Hud from 'app/ui/hud/Hud';
import ScreenOverlayVideo from '../ui/overlays/ScreenOverlayVideo';
import ScreenOverlaySelfie from '../ui/overlays/ScreenOverlaySelfie';
import ScreenOverlayImage from '../ui/overlays/ScreenOverlayImage';
import { GetLocationVideoUrl, GetLocationSouvenirUrl } from '../constants/AppUrls';
import LocationDisplay from '../location/LocationDisplay';
import TutorialOverlay from '../ui/tutorial/TutorialOverlay';
import ScreenOverlayPoll from '../ui/overlays/ScreenOverlayPoll';

/*

  Use this for the main location state.
  The location loader should load in assets as needed via the assetpach found in assets/locations.

  The lcoation id should be passed in through the init method and utilised to handle location accordingly

*/

export default class Location extends Phaser.State {
  init({ locationid }) {
    this.locationid = locationid;
  }

  create() {
    this.container = new ScreenContainer(this.game, {
      title: 'LOCATION : ' + this.locationid,
    });
    this.game.add.existing(this.container);

    // move location content into location display
    // it creates the background and positions locations buttons
    // as well as dispatch that position has changed
    this.locationDisplay = new LocationDisplay(this.game, this.locationid);
    this.locationDisplay.onPositionChanged.add(this.updateCycleButtons, this);
    this.locationDisplay.onActivitySelected.add(this.handleLocationActivity, this);
    this.game.add.existing(this.locationDisplay);

    this.hud = new Hud(this.game, Hud.LOCATION);
    this.hud.onButtonSelect.add(this.handleHudButton, this);
    this.game.add.existing(this.hud);

    // Overlay
    this.bgOverlay = this.game.add.image(0, 0, 'background_alt_overlay');
    this.bgOverlay.anchor.set(0.5);
    this.bgOverlay.x = this.game.defaultWidth * 0.5;
    this.bgOverlay.y = this.game.defaultHeight * 0.5;
    this.mapOverlay = this.game.make.group();
    this.mapOverlay.fixedToCamera = true;
    this.mapOverlay.cameraOffset.x = (this.game.width - this.game.defaultWidth) / 2;
    this.mapOverlay.cameraOffset.y = (this.game.height - this.game.defaultHeight) / 2;
    this.mapOverlay.add(this.bgOverlay);

    // flag for when revealing buttons
    //this is a canned animation, so using this flag to prevent other things such as dragging :)
    this.allowUserNavigation = false;

    // added the nextpage/prevpage buttons to hud
    // we can use this for the scroll buttons :)
    this.btnNext = this.hud.getButton('nextpage');
    this.btnPrev = this.hud.getButton('prevpage');
    this.updateCycleButtons();

    // mark as visited
    this.game.gameController.player.setLocationVisited(this.locationid);

    // this.suitcase = new Suitcase(this.game);
    if (this.game.gameController.player.getTutorialSeen(TUTORIAL_LOCATION) == false) {
      this.showOnboarding();
    } else {
      this.checkForButtonReveal();
    }

    // play music
    this.game.sounds.setBackgroundMusic('music_map', true);
  }

  showOnboarding() {
    const tutorial = [
      {
        copy: this.game.copy.getCopy('onboarding_location_01'),
        image: 'tutorial_location_01',
      },
      {
        copy: this.game.copy.getCopy('onboarding_location_02'),
        image: 'tutorial_location_02',
      },
    ];
    this.tutorial = new TutorialOverlay(this.game, tutorial);
    this.tutorial.onComplete.add(this.handleOnboardingComplete, this);
    this.game.gameController.player.setTutorialSeen(TUTORIAL_LOCATION);
    this.game.add.existing(this.tutorial);
  }

  handleOnboardingComplete() {
    if (this.tutorial) {
      this.tutorial.destroy(true);
      this.tutorial.onComplete.remove(this.handleOnboardingComplete, this);
      this.tutorial = null;
    }
    this.allowUserNavigation = true;
    this.updateCycleButtons();
  }

  // This state uses some 'custom functionality' but utilises the hud for default buttons
  // handle the callback basedon button id
  handleHudButton(btnid, btn) {
    if (this.allowUserNavigation) {
      switch (btnid) {
        case 'nextpage':
          this.locationDisplay.panLeft(this.game.width * 0.25);
          break;
        case 'prevpage':
          this.locationDisplay.panRight(this.game.width * 0.25);
          break;
      }
    }
  }

  updateCycleButtons() {
    if (this.allowUserNavigation === false) {
      this.btnPrev.visible = false;
      this.btnNext.visible = false;
    } else {
      this.btnPrev.visible = this.locationDisplay.canMoveRight();
      this.btnNext.visible = this.locationDisplay.canMoveLeft();
    }
  }

  /*
  ==================================================================================
    USER EVENTS
  ==================================================================================
  */

  checkForButtonReveal() {
    const reveal = this.locationDisplay.checkActiveButtons();
    if (reveal.length > 0) {
      this.revealButtons(reveal);
    } else {
      this.allowUserNavigation = true;
    }
  }

  revealButtons(reveal) {
    const btn = reveal.shift();

    this.allowUserNavigation = false;
    btn.alpha = 0;
    btn.visible = true;

    this.revealTween = this.locationDisplay.tweenToPosistion(
      Math.min(0, -btn.x + (this.game.width * 0.5 - btn.width * 0.5))
    );
    this.revealTween.onComplete.add(this.handleTweenedToButton, this, 0, btn);
  }

  handleTweenedToButton(a, b, btn) {
    btn.anchor.set(0.5);
    btn.x += btn.width * 0.5;
    btn.y += btn.height * 0.5;
    btn.alpha = 1;
    btn.scale.set(0);
    btn.visible = true;
    const btnA = this.game.add.tween(btn.scale).to({ x: 2, y: 2 }, 100, Phaser.Easing.OUT);
    const btnB = this.game.add.tween(btn.scale).to({ x: 1, y: 1 }, 100, Phaser.Easing.IN);
    this.game.camera.shake(0.05, 50, true);
    btnA.chain(btnB);
    btnA.start();
    this.allowUserNavigation = true;
    this.updateCycleButtons();
  }

  handleLocationActivity(activityid, locationid) {
    switch (activityid) {
      case LocationDisplay.VIDEO:
        this.handleShowVideo();
        break;
      case LocationDisplay.POLL:
        this.handleShowPoll();
        break;
      case LocationDisplay.SELFIE:
        this.handleShowSelfie();
        break;
      case LocationDisplay.SOUVENIR:
        this.handleShowSouvenir();
        break;
      case LocationDisplay.FOOD_GAME:
        this.handlePlayFoodGame();
        break;
      case LocationDisplay.CHASE_GAME:
        this.handlePlayChaseGame();
        break;
      case LocationDisplay.LEAVE:
        this.handleExitLocation();
        break;
    }
  }

  handlePlayFoodGame() {
    this.game.sounds.stopBackgroundMusic();

    this.game.gameController.player.setFoodGamePlayed(this.locationid);
    this.game.gameController.triggerMiniGameFood();
  }
  handlePlayChaseGame() {
    this.game.sounds.stopBackgroundMusic();
    this.game.gameController.player.setChaseGamePlayed(this.locationid);
    this.game.gameController.triggerMiniGameChase();
  }
  handleExitLocation() {
    this.game.gameController.player.setLocationVisited(this.locationid);
    this.game.gameController.triggerLocationExit(this.locationid);
  }
  handleViewSuitcase() {
    this.game.gameController.triggerPlayerSuitcase(STATE_LOCATION);
  }
  handleShowPoll() {
    this.game.gameController.player.setPollSeen(this.locationid);
    this.addOverlay(new ScreenOverlayPoll(this.game, { locationid: this.locationid }));
  }

  handleShowVideo() {
    this.game.gameController.player.setVideoSeen(this.locationid);
    this.addOverlay(
      new ScreenOverlayVideo(this.game, {
        videourl: GetLocationVideoUrl(this.locationid),
        locationid: this.locationid,
      })
    );
  }

  handleShowSelfie() {
    this.game.gameController.player.setSelfieSeen(this.locationid);
    console.log('handleShowSelfie', this.locationid);
    this.addOverlay(
      new ScreenOverlaySelfie(this.game, {
        locationid: this.locationid,
      })
    );
  }

  handleShowSouvenir() {
    this.game.gameController.player.setSouvenirSeen(this.locationid);
    this.addOverlay(
      new ScreenOverlayImage(this.game, {
        imageurl: GetLocationSouvenirUrl(this.locationid),
      })
    );

    this.game.sounds.playSpriteSFX('Sh_HeyLook', { spriteID: 'general_vo' });
  }

  /*
  ==================================================================================
    ADD / REMOVE OVERLAYS
  ==================================================================================
  */

  addOverlay(overlay) {
    if (this.overlay) {
      this.handleOverlayClosed();
    }
    this.overlay = overlay;
    this.overlay.onScreenClosed.add(this.handleOverlayClosed, this);
    this.game.add.existing(this.overlay);
    this.hud.setOverlayMode(true);
    this.allowUserNavigation = false;
  }

  handleOverlayClosed() {
    if (this.overlay) {
      this.overlay.onScreenClosed.remove(this.handleOverlayClosed, this);
      this.overlay.destroy();
      this.overlay = null;
    }
    this.hud.setOverlayMode(false);
    this.allowUserNavigation = true;
    this.checkForButtonReveal();
  }

  /*
  ==================================================================================
    Update Function
  ==================================================================================
  */

  update() {
    // implemented draggable location
    // it's ok...
    if (this.game.input.activePointer.isDown && this.allowUserNavigation === true) {
      if (this.game.origDragPoint) {
        let offset = this.locationDisplay.cameraOffset.x;
        let distance = this.game.origDragPoint.x - this.game.input.activePointer.position.x;

        let updatedPath = this.locationDisplay.cameraOffset.x - distance;
        if (updatedPath != offset) {
          if (updatedPath > offset) {
            // if greater then the user is dragging right
            // move BG left
            let moveLeft = Math.min(0, this.locationDisplay.cameraOffset.x - distance);
            if (moveLeft < 0) {
              this.locationDisplay.cameraOffset.x = this.locationDisplay.cameraOffset.x - distance;
            }
            this.btnNext.visible = true;
            this.btnPrev.visible = moveLeft < -50;
          } else {
            // if less than the user is dragging left
            // move BG right
            let moveRight = Math.max(
              this.game.width - this.locationDisplay.background.width,
              this.locationDisplay.cameraOffset.x - distance
            );
            if (this.game.width - this.locationDisplay.background.width < moveRight) {
              this.locationDisplay.cameraOffset.x = this.locationDisplay.cameraOffset.x - distance;
            }
            this.btnPrev.visible = true;
            this.btnNext.visible =
              moveRight > this.game.width - this.locationDisplay.background.width + 50;
          }
        }
      }
      // set new drag origin to current position
      this.game.origDragPoint = this.game.input.activePointer.position.clone();
    } else {
      this.game.origDragPoint = null;
    }
  }

  /*
  ==================================================================================
    CLEAN UP
  ==================================================================================
  */

  shutdown() {
    if (this.overlay) {
      this.overlay.onScreenClosed.remove(this.handleOverlayClosed, this);
      this.overlay.destroy();
      this.overlay = null;
    }

    if (this.tutorial) {
      this.tutorial.destroy(true);
      this.tutorial.onComplete.remove(this.handleOnboardingComplete, this);
      this.tutorial = null;
    }
    this.locationDisplay.onPositionChanged.add(this.updateCycleButtons, this);
    this.locationDisplay.destroy();
    this.locationDisplay = null;

    this.hud.onButtonSelect.remove(this.handleHudButton, this);
    this.hud.destroy(true);

    this.hud = null;
  }
}



// WEBPACK FOOTER //
// ./js/app/states/Location.js